import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { decryptCustomerData } from '../../utils/encryption';

const PhonePePayment: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [amountInRupees, setAmountInRupees] = useState<number>(10);
  const [amountInPaisa, setAmountInPaisa] = useState<number>(1000);
  const [merchantOrderId, setMerchantOrderId] = useState<string>('');
  const [customerData, setCustomerData] = useState<{
    phone?: string;
    email?: string;
    customerId?: string;
    name?: string;
  }>({});

  useEffect(() => {
    // Clear any previous errors
    setError('');

    // Fix malformed URLs (handle duplicate payment.html in URL)
    let searchString = window.location.search;
    if (searchString.includes('payment.html?')) {
      searchString = searchString.replace(/payment\.html\?/g, '?').replace(/\?\?/g, '?');
      const newUrl = window.location.pathname + searchString;
      if (window.location.search !== searchString) {
        window.history.replaceState({}, '', newUrl);
      }
    }

    // Get parameters from URL
    const amountParam = searchParams.get('amount'); // Amount in rupees
    const amountInPaisaParam = searchParams.get('amountPaisa'); // Amount directly in paisa
    
    // Get encrypted customer data or individual parameters
    const encryptedData = searchParams.get('data'); // Encrypted customer data
    let customerId: string | null = null;
    let customerPhone: string | null = null;
    let customerEmail: string | null = null;
    let customerName: string | null = null;

    // Try to decrypt customer data first
    if (encryptedData) {
      try {
        const decrypted = decryptCustomerData(encryptedData);
        customerId = decrypted.customerId || null;
        customerPhone = decrypted.phone || null;
        customerEmail = decrypted.email || null;
        customerName = decrypted.name || null;
        setCustomerData(decrypted);
      } catch (error) {
        console.error('Failed to decrypt customer data:', error);
        // Fallback to individual parameters if decryption fails
        customerId = searchParams.get('customerId');
        customerPhone = searchParams.get('phone');
        customerEmail = searchParams.get('email');
        customerName = searchParams.get('name');
        setCustomerData({
          customerId: customerId || undefined,
          phone: customerPhone || undefined,
          email: customerEmail || undefined,
          name: customerName || undefined,
        });
      }
    } else {
      // Use individual parameters if encrypted data not provided
      customerId = searchParams.get('customerId');
      customerPhone = searchParams.get('phone');
      customerEmail = searchParams.get('email');
      customerName = searchParams.get('name');
      setCustomerData({
        customerId: customerId || undefined,
        phone: customerPhone || undefined,
        email: customerEmail || undefined,
        name: customerName || undefined,
      });
    }

    // Generate order ID with customer info if available
    const timestamp = Date.now();
    const randomSuffix = Math.floor(Math.random() * 10000);

    let orderId = searchParams.get('orderId');

    // If orderId provided, append timestamp and random to make it unique
    if (orderId) {
      orderId = `${orderId}_${timestamp}_${randomSuffix}`;
    } else {
      // Generate new order ID
      orderId = customerId
        ? `ORDER_${customerId}_${timestamp}_${randomSuffix}`
        : `ORDER_${timestamp}_${randomSuffix}`;
    }

    setMerchantOrderId(orderId);

    // Calculate amount in paisa
    let calculatedAmountInPaisa: number;
    let calculatedAmountInRupees: number;

    if (amountInPaisaParam) {
      // Amount provided directly in paisa
      calculatedAmountInPaisa = parseInt(amountInPaisaParam, 10);
      calculatedAmountInRupees = calculatedAmountInPaisa / 100;
    } else if (amountParam) {
      // Amount provided in rupees, convert to paisa
      calculatedAmountInRupees = parseFloat(amountParam);
      calculatedAmountInPaisa = Math.round(calculatedAmountInRupees * 100);
    } else {
      // Default: ₹10 (1000 paisa)
      calculatedAmountInRupees = 10;
      calculatedAmountInPaisa = 1000;
    }

    // Ensure amount is always a positive integer in paisa
    calculatedAmountInPaisa = Math.max(1, Math.round(calculatedAmountInPaisa));
    setAmountInPaisa(calculatedAmountInPaisa);
    setAmountInRupees(calculatedAmountInRupees);
  }, [searchParams]);

  const initiatePayment = async () => {
    setLoading(true);
    setError('');

    try {
      const redirectUrl = `${window.location.origin}/payment-callback`;

      // Use decrypted customer data
      const customerId = customerData.customerId;
      const customerPhone = customerData.phone;
      const customerEmail = customerData.email;

      // Call backend API to initiate payment
      const response = await fetch('https://app.frontdesk24.com/api/phonepe/payment/initiate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          merchantOrderId: merchantOrderId,
          amount: amountInPaisa, // ALWAYS in paisa
          redirectUrl: redirectUrl,
          // Include customer info for tracking
          merchantUserId: customerId || undefined,
          mobileNumber: customerPhone || undefined,
          email: customerEmail || undefined,
        }),
      });

      const data = await response.json();

      if (data.success && data.redirectUrl) {
        // Redirect to PhonePe payment page
        window.location.replace(data.redirectUrl);
      } else {
        // Show error with more details
        const errorMessage = data.error || 'Failed to initiate payment. Please try again.';
        console.error('Payment initiation failed:', data);
        setError(errorMessage);
        setLoading(false);
      }
    } catch (err: any) {
      console.error('Payment initiation error:', err);
      setError('Network error. Please check your connection and try again.');
      setLoading(false);
    }
  };

  // Allow Enter key to trigger payment
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !loading) {
        initiatePayment();
      }
    };

    document.addEventListener('keypress', handleKeyPress);
    return () => {
      document.removeEventListener('keypress', handleKeyPress);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  return (
    <div className="min-h-screen flex items-center justify-center p-5"
      style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      }}
    >
      <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-10 max-w-md w-full text-center">
        <div className="text-4xl mb-5">💳</div>
        <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
          Pay Now
        </h1>
        <p className="text-gray-600 text-sm mb-8">Secure payment via PhonePe</p>

        <div
          className="bg-gray-50 rounded-xl p-6 md:p-8 my-8"
        >
          <div className="text-gray-600 text-sm mb-2">Amount to Pay</div>
          <div className="text-4xl md:text-5xl font-bold text-gray-800 mb-1">
            ₹{amountInRupees.toFixed(2)}
          </div>
          <div className="text-gray-400 text-xs">{amountInPaisa} paisa</div>
        </div>

        <button
          onClick={initiatePayment}
          disabled={loading}
          className="w-full py-4 px-6 rounded-xl text-white font-semibold text-lg transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed hover:shadow-lg active:scale-[0.98]"
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          }}
        >
          {loading ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              <span>Processing...</span>
            </>
          ) : (
            <>
              <span>Pay Now</span>
              <span>→</span>
            </>
          )}
        </button>

        {loading && (
          <div className="mt-5">
            <div className="w-8 h-8 border-3 border-gray-200 border-t-purple-600 rounded-full animate-spin mx-auto"></div>
          </div>
        )}

        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm text-center">
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

export default PhonePePayment;

